import {useCallback} from 'react';
import {useBlocker} from 'react-router-dom';
import {LocalStorage} from '../../../utils/LocalStorage';
import {shouldBlockLeave} from '../informesPaths';

/**
 * El aviso al salir del editor DENTRO de la app —el menú lateral, las solapas, la miga, «atrás»—
 * con trabajo sin guardar, que se descartaba en silencio (card 7yIKxqZj). La guardia del navegador
 * (cerrar o recargar la pestaña) es otra y sigue en `InformesPage`.
 *
 * `enabled` es el marco 2.1: con las solapas a la vista, salir por error deja de ser raro; el
 * clásico queda como estaba.
 *
 * `unsavedWorkRef` es un ref para que la función que se le pasa al router sea ESTABLE: el router la
 * vuelve a registrar cada vez que cambia, y con el booleano cambiaría con cada tecla. El ref tiene el
 * valor del último render. Ojo: NO es lo que deja pasar el guardado. El editor navega en el mismo
 * tick en que guarda, con el ref todavía en `true`; lo que lo deja pasar es la marca
 * `INTERNAL_NAV_STATE` con REPLACE (review C-rec-fe-informes-05).
 *
 * La sesión se lee al navegar: cerrar sesión la borra antes de ir a `/login` (C-rec-fe-informes-01).
 *
 * `blocked`: hay una salida frenada esperando respuesta. `stay` la cancela; `leave` la deja seguir.
 */
export function useLeaveGuard(enabled, unsavedWorkRef) {
    const shouldBlock = useCallback(
        (args) => !!enabled && shouldBlockLeave({
            ...args,
            hasUnsavedWork: !!unsavedWorkRef.current,
            hasSession: LocalStorage.hasUserData(),
        }),
        [enabled, unsavedWorkRef],
    );
    const blocker = useBlocker(shouldBlock);
    const blocked = blocker.state === 'blocked';
    const stay = useCallback(() => { if (blocker.state === 'blocked') blocker.reset(); }, [blocker]);
    const leave = useCallback(() => { if (blocker.state === 'blocked') blocker.proceed(); }, [blocker]);
    return {blocked, stay, leave};
}
